/**
 * Article Key Takeaways
 * 
 * Wirecutter-style decision framework at top of article.
 * Content is 100% extracted from the article itself — unique per page.
 * CTA links to best matching hub page or collection (money pages only, verified from Supabase).
 * 
 * Only rendered if quality score >= 50 (enough content for meaningful takeaways).
 */

import Link from 'next/link'
import type { ExtractedContent } from '@/lib/article-content-extractor'
import type { VerifiedHubPage } from '@/lib/blog-hub-linker'
import type { VerifiedTool } from '@/lib/blog-tool-linker'

interface ArticleKeyTakeawaysProps {
  extractedContent: ExtractedContent
  hubPages: VerifiedHubPage[]
  tools: VerifiedTool[]
}

export function ArticleKeyTakeaways({ extractedContent, hubPages, tools }: ArticleKeyTakeawaysProps) {
  const { keySentences, allDataPoints, qualityScore, subject } = extractedContent

  // Only show if quality is sufficient
  if (qualityScore < 50 || keySentences.length === 0) return null

  // Pick 2-4 takeaways based on quality
  const count = qualityScore >= 80 ? 4 : qualityScore >= 60 ? 3 : 2
  const takeaways = keySentences.slice(0, count)

  // Best money page CTA (prefer hub page, then tool)
  const topHub = hubPages[0]
  const topTool = tools[0]

  return (
    <div className="blog-recommendation-box mb-6">
      {/* FRIDGE-149 §2 — H2, not H3: this box sits directly under the
          article's H1, so an H3 here skipped a level. The look is pinned by
          .blog-recommendation-box__title (!important), not by the tag. */}
      <h2 className="blog-recommendation-box__title">Key Takeaways</h2>
      <ul className="list-disc pl-5 space-y-1.5 mb-3" style={{ color: '#1e3a8a' }}>
        {takeaways.map((sentence, i) => (
          <li key={i} className="text-[0.9375rem] leading-relaxed">{sentence}</li>
        ))}
      </ul>

      {topHub && (
        <Link
          href={topHub.url}
          className="inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold text-blue-700 hover:text-blue-900 transition-colors"
        >
          Browse {topHub.title}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      )}
      {!topHub && topTool && (
        <Link
          href={topTool.url}
          className="inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold text-blue-700 hover:text-blue-900 transition-colors"
        >
          Try the {topTool.name}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      )}
    </div>
  )
}
